import { Archive, ArchiveRestore, Clock, Copy, Download, Eye, FileText, Loader2, Mail, MoreHorizontal, Pencil, Plug, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QuoteStatus } from '@/types';
import type { Quote } from '@/types';
import { getCountersignActionLabel, isOpenQuoteStatus } from './quoteMeta';
import { needsUboCountersign } from './quoteSignals';

export interface QuoteActionHandlers {
  onEdit: () => void;
  onMcpConnect: () => void;
  onView: () => void;
  onSaveAsTemplate: () => void;
  onDuplicate: () => void;
  onDownloadPdf: () => void;
  onHours: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onExtend: () => void;
  onWithdraw: () => void;
  onResendEmail: (type: 'countersign' | 'signed') => void;
  onOpenCountersign: () => void;
}

export interface QuoteBusyState {
  emailResendBusy: string | null;
  countersignLinkBusy: string | null;
  pdfDownloadBusy: string | null;
}

/**
 * Eén menu voor beide weergaven. De lijst en de kaart bieden dezelfde acties
 * aan; ze verschilden eerder per weergave omdat het menu twee keer bestond.
 */
export function QuoteRowActions({
  quote,
  actions,
  busy,
  align = 'end',
}: {
  quote: Quote;
  actions: QuoteActionHandlers;
  busy: QuoteBusyState;
  align?: 'start' | 'center' | 'end';
}) {
  const isExpired = quote.status === QuoteStatus.EXPIRED;
  const isOpen = isOpenQuoteStatus(quote.status);
  const needsCountersign = needsUboCountersign(quote);
  const isPdfDownloading = busy.pdfDownloadBusy === quote.id;
  const emailBusy = Boolean(busy.emailResendBusy?.startsWith(`${quote.id}:`));

  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 shrink-0 p-0 text-muted-foreground"
        aria-label="Meer acties"
        onClick={(e) => e.stopPropagation()}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
  );

  /* Gearchiveerd: de klantlink staat uit, dus alleen wat intern nog zin heeft. */
  if (quote.archivedAt) {
    return (
      <DropdownMenu>
        {trigger}
        <DropdownMenuContent align={align} className="w-60">
          <DropdownMenuItem onClick={actions.onRestore}>
            <ArchiveRestore className="h-4 w-4" />
            Terugzetten
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={actions.onEdit}>
            <Pencil className="h-4 w-4" />
            Bewerken
          </DropdownMenuItem>
          <DropdownMenuItem onClick={actions.onDownloadPdf} disabled={Boolean(busy.pdfDownloadBusy)}>
            {isPdfDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isPdfDownloading ? 'PDF genereren...' : 'PDF downloaden'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={actions.onHours}>
            <Clock className="h-4 w-4" />
            Urenoverzicht
          </DropdownMenuItem>
          <DropdownMenuItem onClick={actions.onSaveAsTemplate}>
            <FileText className="h-4 w-4" />
            Opslaan als template
          </DropdownMenuItem>
          <DropdownMenuItem onClick={actions.onDuplicate}>
            <Copy className="h-4 w-4" />
            Dupliceren
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      {trigger}
      <DropdownMenuContent align={align} className="w-60">
        <DropdownMenuItem onClick={actions.onEdit}>
          <Pencil className="h-4 w-4" />
          Bewerken
        </DropdownMenuItem>
        <DropdownMenuItem onClick={actions.onMcpConnect}>
          <Plug className="h-4 w-4" />
          Verbinden via MCP
        </DropdownMenuItem>
        <DropdownMenuItem onClick={actions.onView} disabled={!quote.slug}>
          <Eye className="h-4 w-4" />
          Bekijken als klant
        </DropdownMenuItem>
        <DropdownMenuItem onClick={actions.onDownloadPdf} disabled={Boolean(busy.pdfDownloadBusy)}>
          {isPdfDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {isPdfDownloading ? 'PDF genereren...' : 'PDF downloaden'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={actions.onHours}>
          <Clock className="h-4 w-4" />
          Urenoverzicht
        </DropdownMenuItem>
        {needsCountersign && (
          <DropdownMenuItem
            onClick={actions.onOpenCountersign}
            disabled={busy.countersignLinkBusy === quote.id}
            className="text-[#b8600b] focus:text-[#b8600b]"
          >
            {busy.countersignLinkBusy === quote.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Pencil className="h-4 w-4" />
            )}
            {getCountersignActionLabel(quote)}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={actions.onSaveAsTemplate}>
          <FileText className="h-4 w-4" />
          Opslaan als template
        </DropdownMenuItem>
        <DropdownMenuItem onClick={actions.onDuplicate}>
          <Copy className="h-4 w-4" />
          Dupliceren
        </DropdownMenuItem>
        {isExpired && (
          <DropdownMenuItem onClick={actions.onExtend}>
            <RefreshCw className="h-4 w-4" />
            Verlengen
          </DropdownMenuItem>
        )}
        {quote.slug && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <Mail className="h-4 w-4" />
              E-mail opnieuw versturen
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[14rem]">
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  actions.onResendEmail('countersign');
                }}
                disabled={emailBusy}
              >
                {busy.emailResendBusy === `${quote.id}:countersign` ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Medeondertekening (UBO)
              </DropdownMenuItem>
              {quote.status === QuoteStatus.ACCEPTED && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    actions.onResendEmail('signed');
                  }}
                  disabled={emailBusy}
                >
                  {busy.emailResendBusy === `${quote.id}:signed` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Teamnotificatie (deal owner)
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuSeparator />
        {isOpen && (
          <DropdownMenuItem onClick={actions.onWithdraw} className="text-destructive focus:text-destructive">
            <RotateCcw className="h-4 w-4" />
            Intrekken
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={actions.onArchive}>
          <Archive className="h-4 w-4" />
          Archiveren
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default QuoteRowActions;
